import React, { useState, useEffect, useMemo } from 'react';
import { Box, List, ListItem, ListItemText, Stack, Typography, Alert, Avatar, ListItemAvatar, useTheme, Switch, Pagination, TextField } from '@mui/material';
import { useProducts } from '@features/products/hooks/useProducts';
import { SubscriptionPlan } from '@domain/subscription/models/SubscriptionPlan';
import { Product } from '@domain/product/models/Product';
import { Paper as CustomPaper } from '@shared/ui';
import { useProductUpdate } from '@features/products/hooks/useProductUpdate';

interface SubscriptionProductSelectionProps {
    selectedPlan: SubscriptionPlan;
    exceedsLimitBy: number;
    productsCount: number;
    activeProducts: Product[];
}

export const SubscriptionProductSelection: React.FC<SubscriptionProductSelectionProps> = ({ selectedPlan, exceedsLimitBy, productsCount, activeProducts }) => {
    const theme = useTheme();

    const [page, setPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(50);

    const [search, setSearch] = useState('');

    const maxProductsFeature = selectedPlan.features?.find(f => f.featureKey === 'max_products')
    const maxProductsLimit = maxProductsFeature?.limitValue === -1 ? Infinity : maxProductsFeature?.limitValue || 0;

    const { products } = useProducts({ page, limit: pageLimit, search });
    const initialActiveProductsCount = useMemo(() => activeProducts?.filter(p => p.active).length || 0, []);

    const { updateProduct } = useProductUpdate();

    const [isUpdatingProducts, setIsUpdatingProducts] = useState<Product | null>(null);

    const toggleProductActive = async (product: Product) => {
        setIsUpdatingProducts(product);
        try {
            await updateProduct({ productId: product.id, updateData: { active: !product.active } });
        } catch (error) {
            console.error('Failed to update products:', error);
        } finally {
            setIsUpdatingProducts(null);
        }
    };


    if (initialActiveProductsCount > maxProductsLimit) {
        return (
            <CustomPaper>
                <Stack spacing={2} sx={{ p: 3, position: 'relative' }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Downgrade to a {selectedPlan.name} plan
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Within the {selectedPlan.name} plan, you can have up to {selectedPlan.features?.find(f => f.featureKey === 'max_products')?.limitValue === -1 ? 'unlimited' : selectedPlan.features?.find(f => f.featureKey === 'max_products')?.limitValue} products.
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            To downgrade to a {selectedPlan.name} plan, you need to mark products as unreplenishable.
                        </Typography>
                    </Box>

                    <Stack position="sticky" alignItems="center" top={20} spacing={2} direction="row"
                        sx={{
                            boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
                            backgroundColor: theme.palette.background.paper,
                            zIndex: 1000,
                            borderRadius: 1,
                            border: 1,
                            borderColor: theme.palette.divider,
                            p: 2,
                        }}
                    >

                        {exceedsLimitBy > 0 ? (
                            <Alert
                                sx={{
                                    flexGrow: 1,
                                    border: '1px solid',
                                    borderColor: 'error.main',
                                }}
                                severity="error">
                                <Typography variant="body2" color="text.secondary">
                                    You have {productsCount} products, which exceeds the new limit of {maxProductsLimit === -1 ? 'unlimited' : maxProductsLimit}.
                                </Typography>
                            </Alert>
                        ) : (
                            <Alert
                                sx={{
                                    flexGrow: 1,
                                    border: '1px solid',
                                    borderColor: 'success.main',
                                }}
                                severity="success">
                                <Typography variant="body2" color="text.secondary">
                                    You have {productsCount} products, which is within the new limit of {maxProductsLimit === -1 ? 'unlimited' : maxProductsLimit}.
                                </Typography>
                            </Alert>

                        )}

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Search products"
                                value={search}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                                size="small"
                                autoFocus
                            />
                        </Stack>
                    </Stack>

                    <List sx={{
                        overflow: 'hidden'
                    }}>
                        {products?.status === 'success' && products?.data?.map((product) => (
                            <React.Fragment key={product.id}>
                                <ListItem
                                    divider
                                    disablePadding
                                    sx={{
                                        py: 2,
                                        gap: 4,
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            src={product.image}
                                            variant="rounded"
                                            sx={{ width: 40, height: 40, border: 1, borderColor: theme.palette.divider }}
                                        />
                                    </ListItemAvatar>

                                    <ListItemText

                                        primary={
                                            <Box>
                                                <Typography variant="body1"
                                                    color={product.active ? theme.palette.text.primary : theme.palette.text.secondary}
                                                >
                                                    {product.title || `Product ${product.id}`}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="body2" color="text.secondary">
                                                EAN: {product.id}
                                            </Typography>
                                        }
                                    />

                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="body2"
                                            color={product.active ? theme.palette.text.primary : theme.palette.text.secondary}>
                                            Replenishable
                                        </Typography>

                                        <Switch
                                            checked={product.active}
                                            onChange={() => toggleProductActive(product)}
                                            disabled={isUpdatingProducts?.id === product.id}
                                        />
                                    </Stack>
                                </ListItem>


                            </React.Fragment>
                        ))}
                    </List>

                    <Stack p={2} direction="row" justifyContent="center" alignItems="center">
                        <Pagination
                            count={products?.status === 'success' ? products?.totalPages : 0}
                            page={page}
                            onChange={(event, value) => setPage(value)}
                            color="primary"
                            size="large"
                            shape="rounded"
                            showFirstButton
                            showLastButton
                        />
                    </Stack>
                </Stack>

            </CustomPaper>
        )
    }

    return (
        <></>
    );
};
